import { Suspense, lazy, useEffect, useRef, useState, type FormEvent } from 'react'
import { ShieldCheck, Wallet } from 'lucide-react'

import { ApiError } from '../../core/errors'
import type { WalletKind } from '../../core/wallet'
import type { LoginDto } from '../../data/types'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Field'
import { ENTRY_DESTINATIONS, type EntryDestinations } from './destination'
import { LegalFooter } from '../../ui/LegalFooter'
import { LogoMark } from '../../ui/Logo'
import { Lattice } from '../../app/ambient/LatticeCanvas'
import { installPointerLight } from '../../app/ambient/pointerLight'

const WalletRecoverySheet = lazy(() =>
  import('../wallet/WalletRecoverySheet').then((module) => ({ default: module.WalletRecoverySheet })),
)

type LoginPageProps = {
  onSubmit: (credentials: { username: string; password: string }) => Promise<LoginDto>
  onSignup?: (credentials: { email: string; password: string }) => Promise<LoginDto>
  onSuccess?: () => void
  onWalletLogin?: (kind: WalletKind) => Promise<LoginDto>
  /** Stop waiting for the wallet sign-in in flight. */
  onCancelWalletLogin?: () => void
  onGoogleLogin?: () => void
  wallets?: WalletKind[]
  /** Where a completed entry lands when the host supplies no `onSuccess`. */
  destinations?: EntryDestinations
}

/**
 * Sign-up and sign-in, stated as the two stages they actually are.
 *
 * Plan §4.2: email and password open an explorable workspace; Keplr or Leap
 * activates personal balances and real rentals. The page says that in one place
 * instead of leaving a visitor to infer it from a disabled button — the old
 * "Keplr or Leap not detected" control, which explained nothing, offered
 * nothing, and was the sign-in half of the silent-failure problem.
 *
 * Wallet entry now opens the shared recovery sheet, so every §4.4 state —
 * missing, locked, rejected, wrong chain, connecting — reaches the same surface
 * the workspace uses, with the form behind it untouched. No authentication
 * behaviour changed: the same challenge/verify callback runs, and the console
 * still never sees a key.
 */
export function LoginPage({
  onSubmit,
  onSignup,
  onSuccess,
  onWalletLogin,
  onCancelWalletLogin,
  onGoogleLogin,
  wallets = [],
  destinations = ENTRY_DESTINATIONS,
}: LoginPageProps) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [error, setError] = useState(() => new URLSearchParams(window.location.search).get('error') ?? '')
  const [busy, setBusy] = useState(false)
  const [walletOpen, setWalletOpen] = useState(false)
  const [walletBusy, setWalletBusy] = useState(false)
  const [walletError, setWalletError] = useState<unknown>(null)
  const walletEverOpened = useRef(false)
  const errorRef = useRef<HTMLParagraphElement>(null)
  const isSignup = mode === 'signup'

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    if (!form.reportValidity()) return
    const values = new FormData(form)
    const password = String(values.get('password') ?? '')
    setError('')
    setBusy(true)
    try {
      let result: LoginDto
      if (isSignup) {
        const email = String(values.get('email') ?? '').trim()
        const confirmation = String(values.get('password-confirmation') ?? '')
        if (password !== confirmation) {
          throw new Error('Passwords do not match.')
        }
        if (!onSignup) {
          throw new Error('Sign up is not available right now.')
        }
        result = await onSignup({ email, password })
      } else {
        result = await onSubmit({ username: String(values.get('identifier') ?? '').trim(), password })
      }
      if (result.status !== 'success') throw new Error(result.message || (isSignup ? 'Could not create your account.' : 'Invalid email or password.'))
      if (onSuccess) onSuccess()
      // The workspace, not the public introduction: a new account that lands
      // back on the landing page has been given no way in.
      else window.location.assign(destinations.afterEmailEntry)
    } catch (cause) {
      // A 401 on this public form means the credentials were rejected, not
      // that an existing session expired (the shared ApiError copy is aimed at
      // authenticated API calls).
      const message = cause instanceof ApiError
        ? (cause.status === 401 ? 'Invalid email or password.' : cause.explanation)
        : cause instanceof Error ? cause.message : 'The console is unreachable. Try again in a moment.'
      setError(message)
      window.setTimeout(() => errorRef.current?.focus(), 0)
    } finally {
      setBusy(false)
    }
  }

  const walletLogin = async (kind: WalletKind) => {
    if (!onWalletLogin) return
    setWalletError(null)
    setWalletBusy(true)
    try {
      const result = await onWalletLogin(kind)
      if (result.status !== 'success') throw new Error(result.message || 'Wallet sign-in did not complete.')
      if (onSuccess) onSuccess()
      else window.location.assign(destinations.afterWalletEntry)
    } catch (cause) {
      // The sheet classifies a WalletError itself; anything else is flattened
      // to a sentence first, because ApiError's 401 copy ("your session has
      // expired") is written for authenticated calls and is simply untrue of a
      // rejected challenge on a page nobody is signed in to.
      setWalletError(cause instanceof ApiError ? new Error(cause.detail || cause.message) : cause)
    } finally {
      setWalletBusy(false)
    }
  }

  useEffect(() => installPointerLight(), [])

  const toggleMode = () => {
    setMode((current) => current === 'signin' ? 'signup' : 'signin')
    setError('')
  }

  return <div className="entry-layout">
    <div className="entry-sky" aria-hidden="true"><Lattice mode="entry" /></div>
    <header className="entry-bar">
      <div className="entry-brand">
        <LogoMark className="entry-mark" />
        <span className="entry-brand-copy"><strong>DDWS</strong><span>Cloud workspace</span></span>
      </div>
    </header>

    <main className="entry-main">
      <section className="entry-card" aria-labelledby="login-title">
        <h1 id="login-title">{isSignup ? 'Create your workspace' : 'Welcome back'}</h1>
        <p id="login-description" className="entry-subtitle">
          {isSignup
            ? 'An email address and a password are all you need to start. No wallet is required to explore.'
            : 'Sign in with the email and password for your workspace, or continue with a wallet you have already activated.'}
        </p>

        {/* Stated once, at the top, so the wallet question is answered before
            it is asked rather than after a control has refused to work. */}
        <ol className="entry-stages" aria-label="How access works">
          <li className="entry-stage">
            <span className="entry-stage-ordinal" aria-hidden="true">1</span>
            <span className="entry-stage-copy">
              <strong>Enter your workspace</strong>
              <small>Email and password open the console and its read-only examples.</small>
            </span>
          </li>
          <li className="entry-stage">
            <span className="entry-stage-ordinal" aria-hidden="true">2</span>
            <span className="entry-stage-copy">
              <strong>Activate real usage</strong>
              <small>Keplr or Leap adds your own balance, instances, and signed rentals — whenever you want it.</small>
            </span>
          </li>
        </ol>

        {error && <p ref={errorRef} id="login-error" className="error-msg" role="alert" aria-live="assertive" tabIndex={-1}>{error}</p>}

        <form className="entry-form" aria-describedby="login-description" onSubmit={(event) => void submit(event)}>
          {isSignup
            ? <>
              <Input label="Email address" id="email" name="email" type="email" placeholder="you@example.com" autoComplete="email" required />
              <Input label="Password" id="password" name="password" type="password" placeholder="At least 8 characters" autoComplete="new-password" minLength={8} required />
              <Input label="Confirm password" id="password-confirmation" name="password-confirmation" type="password" placeholder="Re-enter your password" autoComplete="new-password" minLength={8} required />
            </>
            : <>
              <Input label="Email or username" id="identifier" name="identifier" type="text" placeholder="you@example.com" autoComplete="username" required />
              <Input label="Password" id="password" name="password" type="password" placeholder="Enter your password" autoComplete="current-password" required />
            </>}
          <Button id="submit-button" type="submit" variant="primary" size="lg" block loading={busy} disabled={busy}>
            {isSignup ? 'Create workspace' : 'Sign in'}
          </Button>
          {isSignup && <p className="entry-consent">
            By creating a workspace you agree to the <a href="/app/legal/terms">Terms of Service</a> and
            acknowledge the <a href="/app/legal/privacy">Privacy Policy</a>.
          </p>}
        </form>

        <div className="entry-divider" aria-hidden="true"><span>or</span></div>

        <div className="entry-alternatives" aria-label={isSignup ? 'Other ways to sign up' : 'Other ways to sign in'}>
          <Button size="lg" block className="entry-alternative-google" onClick={() => onGoogleLogin ? onGoogleLogin() : window.location.assign('/auth/google/start')}>
            Continue with Google
          </Button>
          {/* Always offered, never disabled: when no extension is installed the
              sheet says so and offers the official downloads and Check again,
              which is the whole point of §4.4. */}
          <Button size="lg" block onClick={() => { walletEverOpened.current = true; setWalletOpen(true) }}>
            <Wallet size={18} strokeWidth={1.8} aria-hidden="true" />
            Continue with a wallet
          </Button>
          <p className="entry-alternative-note">
            {isSignup
              ? 'A wallet is optional here. You can add Keplr or Leap later, from the workspace.'
              : 'Keplr or Leap signs a one-time challenge to prove the chain account is yours.'}
          </p>
        </div>

        <p className="entry-mode-switch">
          {isSignup ? 'Already have a workspace?' : 'New to DDWS?'}{' '}
          <button type="button" onClick={toggleMode}>{isSignup ? 'Sign in' : 'Create one'}</button>
        </p>

        <p className="entry-trust">
          <ShieldCheck size={16} strokeWidth={1.8} aria-hidden="true" />
          Every transaction is signed inside your wallet. This console never holds your key.
        </p>
      </section>
    </main>

    <footer className="entry-footer"><LegalFooter /></footer>

    {walletEverOpened.current && (
      <Suspense fallback={null}>
        <WalletRecoverySheet
          open={walletOpen}
          onOpenChange={setWalletOpen}
          wallets={wallets}
          connecting={walletBusy}
          error={walletError}
          onConnect={(kind) => void walletLogin(kind)}
          onCancel={onCancelWalletLogin}
        />
      </Suspense>
    )}
  </div>
}
